import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from '../services/api';
import { API } from '../services/api';
import {
  ChartLine, ListChecks, Lightbulb, Users, Plus, Check, PushPin,
  Trash, ArrowsClockwise, MagnifyingGlass,
  Archive, ArrowCounterClockwise, Wrench, FileText, CaretDown, CaretUp,
  X, SortAscending, SortDescending, CaretLeft, CaretRight,
  Shield, Warning, Export, Eye, Clock, Lightning, UserCircle,
  ChatCircle, BookOpen, File, Heartbeat, ArrowLeft,
  Funnel, CalendarBlank, UserPlus, Broadcast, Database, CreditCard
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, BarChart, Bar
} from 'recharts';

/* Founder panel gate — uses authoritative `role === 'admin'` flag from the JWT-backed
   /auth/me response. This matches the backend's own admin gate and avoids brittle
   hardcoded email strings that break when the founder's email changes (e.g. the
   2026-04-22 rename from michael@procloud.com.au → michael@kindraai.dev). */
const isFounder = (u) => !!u && u.role === 'admin';

/* ─── Shared Utilities ─── */
const fmtDur = (s) => {
  if (!s) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600 * 10) / 10}h`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

/* ─── Shared Components ─── */
const Tab = ({ active, label, icon: Icon, onClick, testId, count }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors min-h-[36px] whitespace-nowrap ${
      active ? 'bg-[#1a3a30] text-white' : 'text-slate-500 hover:bg-slate-100'
    }`}>
    <Icon size={14} weight={active ? 'fill' : 'regular'} />
    {label}
    {count !== undefined && <span className="text-[9px] opacity-70 ml-0.5">{count}</span>}
  </button>
);

const PBadge = ({ p }) => {
  const s = {
    high: 'bg-red-50 text-red-600 border-red-100',
    medium: 'bg-amber-50 text-amber-600 border-amber-100',
    low: 'bg-slate-50 text-slate-500 border-slate-200',
    critical: 'bg-red-100 text-red-700 border-red-200'
  };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${s[p] || s.medium}`}>{p}</span>;
};

const MetricCard = ({ label, value, sub, accent }) => (
  <div className={`bg-white border rounded-xl p-3 ${accent ? 'border-[#1a3a30]/20' : 'border-slate-200'}`}>
    <p className={`text-xl font-bold tabular-nums ${accent ? 'text-[#1a3a30]' : 'text-[#0F172A]'}`}>{value}</p>
    <p className="text-[10px] text-slate-500 font-medium mt-0.5">{label}</p>
    {sub && <p className="text-[9px] text-slate-400">{sub}</p>}
  </div>
);

const TrustBadge = ({ confidence, trackingSince, daysTracking, totalEvents }) => {
  const colors = {
    STABLE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    BUILDING: 'bg-amber-50 text-amber-700 border-amber-200',
    LOW: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  const tooltips = {
    STABLE: 'Statistically reliable — 14+ days of tracking with 100+ events',
    BUILDING: 'Early-stage data forming patterns — 3-14 days of tracking',
    LOW: 'Insufficient data — less than 3 days or fewer than 10 events',
  };
  return (
    <div className={`flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg border text-[11px] ${colors[confidence] || colors.LOW}`} data-testid="data-trust-badge" title={tooltips[confidence]}>
      <Shield size={14} weight="fill" />
      <span className="font-semibold">Data confidence: {confidence}</span>
      <span className="opacity-70">Tracking since {fmtDate(trackingSince)} ({daysTracking}d)</span>
      {totalEvents > 0 && <span className="opacity-60">{totalEvents} events</span>}
      <span className="opacity-60 ml-auto">Excludes demo & founder accounts</span>
    </div>
  );
};

const ChartTooltip = ({ active, payload, label, suffix }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="text-slate-500 mb-1">{fmtDateShort(label) || label}</p>
      <p className="font-bold text-[#0F172A]">{payload[0].value}{suffix || ''}</p>
    </div>
  );
};


/* ═══════════════════════════════════════
   ANALYTICS TAB — Investor-Ready
   ═══════════════════════════════════════ */
const EmptyChart = ({ trackingSince }) => (
  <div className="flex flex-col items-center justify-center h-[160px] text-center">
    <ChartLine size={24} className="text-slate-300 mb-2" />
    <p className="text-xs text-slate-400 font-medium">Tracking started {fmtDate(trackingSince)}</p>
    <p className="text-[10px] text-slate-300 mt-0.5">Data will populate as users engage</p>
  </div>
);

const hasChartData = (series) => series && series.some(d => d.value > 0);

/* ── Narrative Generator ── */
const generateNarrative = (metrics, trust, signups, dau) => {
  if (!metrics) return [];
  const lines = [];
  const { total_users, active_24h, active_7d, return_rate, avg_session_duration, avg_sessions_per_user } = metrics;
  const durMin = Math.round((avg_session_duration || 0) / 60);
  const days = trust?.days_tracking || 0;

  // Growth trend from signups
  let trend = 'stable';
  if (signups && signups.length >= 6) {
    const recent3 = signups.slice(-3).reduce((s, d) => s + (d.new || 0), 0);
    const prev3 = signups.slice(-6, -3).reduce((s, d) => s + (d.new || 0), 0);
    if (recent3 > prev3 * 1.2) trend = 'accelerating';
    else if (recent3 < prev3 * 0.5 && prev3 > 0) trend = 'slowing';
  }

  // Line 1: headline
  if (total_users >= 10 && return_rate >= 20) {
    lines.push(`Kindra is showing early signs of genuine engagement. ${total_users} users have onboarded in ${days} days, with a ${return_rate}% return rate and an average session time of ${durMin} minutes.`);
  } else if (total_users > 0) {
    lines.push(`Kindra has ${total_users} registered user${total_users > 1 ? 's' : ''} over ${days} days of tracking. Average session duration is ${durMin} minute${durMin !== 1 ? 's' : ''}.`);
  } else {
    lines.push(`Tracking is active but no user signups have been recorded yet.`);
  }

  // Line 2: activity
  if (active_24h > 0) {
    lines.push(`${active_24h} user${active_24h > 1 ? 's' : ''} active in the last 24 hours, ${active_7d} in the last week. Users average ${avg_sessions_per_user} session${avg_sessions_per_user !== 1 ? 's' : ''} each.`);
  } else if (active_7d > 0) {
    lines.push(`${active_7d} user${active_7d > 1 ? 's' : ''} were active this week. No sessions in the last 24 hours.`);
  }

  // Line 3: trend
  if (trend === 'accelerating') {
    lines.push(`User growth has accelerated in the last 3 days, indicating increasing onboarding momentum.`);
  } else if (trend === 'slowing') {
    lines.push(`Signup pace has slowed recently. Focus on activation and onboarding funnel.`);
  } else if (total_users >= 5) {
    lines.push(`User activity is beginning to form consistent daily patterns.`);
  }

  return lines;
};

/* ── Insight Card Generator ── */
const generateInsights = (metrics, feature_usage, trust) => {
  if (!metrics) return [];
  const cards = [];
  const durMin = Math.round((metrics.avg_session_duration || 0) / 60);
  const { return_rate, total_users, active_24h, active_7d, total_sessions } = metrics;

  // Engagement strength
  if (durMin >= 10) {
    cards.push({ title: 'Engagement Strength', type: 'positive', text: `Average session time is ${durMin} minutes — users are actively interacting with their health data, not passively browsing.` });
  } else if (durMin >= 3) {
    cards.push({ title: 'Engagement Signal', type: 'neutral', text: `Average session of ${durMin} minutes. Sessions are short but present — depth may increase as users add more health data.` });
  }

  // Retention
  if (return_rate >= 25) {
    cards.push({ title: 'Retention Signal', type: 'positive', text: `${return_rate}% return rate suggests early habit formation — strong for a pre-scale health platform.` });
  } else if (return_rate > 0) {
    cards.push({ title: 'Retention Forming', type: 'neutral', text: `${return_rate}% return rate. Early stage — retention typically strengthens as users build health profiles.` });
  }

  // Feature adoption
  if (feature_usage) {
    const { users_with_health_data, users_with_chat, total_users: fu_total } = feature_usage;
    if (fu_total > 0 && users_with_health_data > 0) {
      const healthPct = Math.round(users_with_health_data * 100 / fu_total);
      cards.push({ title: 'Health Data Adoption', type: healthPct >= 30 ? 'positive' : 'neutral', text: `${healthPct}% of users have added health conditions — a critical activation signal indicating they trust the platform with personal data.` });
    }
    if (fu_total > 0 && users_with_chat > 0) {
      const chatPct = Math.round(users_with_chat * 100 / fu_total);
      cards.push({ title: 'AI Companion Usage', type: chatPct >= 20 ? 'positive' : 'neutral', text: `${chatPct}% of users have engaged with the AI chat — the core differentiator and value driver.` });
    }
  }

  // Risk signals
  if (total_users >= 5 && active_7d === 0 && total_sessions > 0) {
    cards.push({ title: 'Activation Gap', type: 'risk', text: `No active users this week despite ${total_users} signups. Focus should shift to re-engagement and onboarding completion.` });
  } else if (total_users >= 10 && active_24h === 0) {
    cards.push({ title: 'Daily Engagement Gap', type: 'risk', text: `No users active in the last 24 hours. Consider push notifications or daily prompt improvements.` });
  }

  return cards;
};

const InsightCard = ({ title, type, text, investorMode }) => {
  const colors = {
    positive: 'border-l-emerald-500 bg-emerald-50/30',
    neutral: 'border-l-amber-400 bg-amber-50/20',
    risk: 'border-l-red-400 bg-red-50/20',
  };
  return (
    <div className={`border border-slate-200 border-l-[3px] rounded-lg px-4 py-3 ${colors[type] || colors.neutral}`} data-testid={`insight-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs font-bold text-[#0F172A] mb-1">{title}</p>
      <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
};

const TRUST_TOOLTIPS = {
  LOW: 'Less than 3 days of tracking or fewer than 10 events. Insufficient data for reliable metrics.',
  BUILDING: 'Between 3–14 days of tracking with 10+ events. Early-stage data is forming patterns.',
  STABLE: '14+ days of tracking with 100+ events. Metrics are statistically reliable.',
};

const AnalyticsTab = ({ analytics, loading, onRefresh, userMetrics }) => {
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [liveSessions, setLiveSessions] = useState(null);
  const [investorMode, setInvestorMode] = useState(false);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/founder/live-sessions`);
      setLiveSessions(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadLive();
    const interval = setInterval(loadLive, 15000);
    return () => clearInterval(interval);
  }, [loadLive]);

  const runBackfill = async () => {
    setBackfillLoading(true);
    try {
      const { data } = await axios.post(`${API}/founder/backfill`);
      if (data.status === 'already_backfilled') toast.info('Backfill already completed');
      else { toast.success(`Backfill: ${data.sessions_created} sessions, ${data.events_created} events`); onRefresh(); }
    } catch { toast.error('Backfill failed'); }
    finally { setBackfillLoading(false); }
  };

  if (loading || !analytics) return (
    <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-48 bg-white border border-slate-200 rounded-xl animate-pulse" />)}</div>
  );

  const { dau, wau, sessions_per_day, avg_duration, retention, signups, feature_usage, trust } = analytics;
  const avgDurFormatted = avg_duration.map(d => ({ ...d, value: Math.round(d.value / 60 * 10) / 10 }));
  const m = userMetrics; // shorthand for metrics from /users endpoint
  const narrative = generateNarrative(m, trust, signups, dau);
  const insights = generateInsights(m, feature_usage, trust);

  return (
    <div className="space-y-5" data-testid="founder-analytics">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <TrustBadge confidence={trust.confidence} trackingSince={trust.tracking_since} daysTracking={trust.days_tracking} totalEvents={trust.total_events} />
        <button onClick={() => setInvestorMode(!investorMode)}
          className={`flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-lg border transition-colors ${investorMode ? 'bg-[#1a3a30] text-white border-[#1a3a30]' : 'text-slate-500 border-slate-200 hover:bg-slate-50'}`}
          data-testid="investor-mode-toggle">
          <Eye size={13} />
          {investorMode ? 'Investor View ON' : 'Investor View'}
        </button>
      </div>

      {/* Controls (hidden in investor mode) */}
      {!investorMode && (
        <div className="flex items-center gap-3 flex-wrap">
          {liveSessions && (
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="live-sessions-indicator">
              <Broadcast size={14} className={liveSessions.total_active > 0 ? 'text-emerald-500 animate-pulse' : 'text-slate-300'} weight="fill" />
              <span className="text-xs font-medium text-slate-600">{liveSessions.total_active} live now</span>
            </div>
          )}
          <button onClick={runBackfill} disabled={backfillLoading}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#1a3a30] bg-white border border-slate-200 rounded-lg px-3 py-2 disabled:opacity-40"
            data-testid="backfill-btn">
            <Database size={13} />
            {backfillLoading ? 'Running...' : 'Backfill'}
          </button>
          <button onClick={onRefresh} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowsClockwise size={13} className="text-slate-400" /></button>
        </div>
      )}

      {/* PART 1: Narrative Block */}
      {narrative.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="narrative-block">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-3">What This Means</h3>
          <div className="space-y-2">
            {narrative.map((line, i) => (
              <p key={i} className="text-sm text-slate-600 leading-relaxed">{line}</p>
            ))}
          </div>
        </div>
      )}

      {/* PART 2: Insight Cards */}
      {insights.length > 0 && (
        <div data-testid="insight-cards">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-3">Key Signals</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {insights.map((c, i) => <InsightCard key={i} {...c} investorMode={investorMode} />)}
          </div>
        </div>
      )}

      {/* Metric Summary (compact in investor mode) */}
      {m && (
        <div className="grid grid-cols-3 gap-2">
          <MetricCard label="Total Users" value={m.total_users} accent />
          <MetricCard label="Active 24h" value={m.active_24h} />
          <MetricCard label="Active 7d" value={m.active_7d} />
          <MetricCard label="Avg Session" value={fmtDur(m.avg_session_duration)} />
          <MetricCard label="Return Rate" value={`${m.return_rate}%`} />
          <MetricCard label="Sessions/User" value={m.avg_sessions_per_user} />
        </div>
      )}

      {/* Signups Chart */}
      {signups?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">User Growth</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-signups">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={signups}>
                <defs><linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1a3a30" stopOpacity={0.15}/><stop offset="95%" stopColor="#1a3a30" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" allowDecimals={false} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (<div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs"><p className="text-slate-500 mb-1">{fmtDateShort(label)}</p><p className="font-bold text-[#0F172A]">{d?.total} total users</p>{d?.new > 0 && <p className="text-emerald-600">+{d?.new} new</p>}</div>);
                }} />
                <Area type="stepAfter" dataKey="total" stroke="#1a3a30" strokeWidth={2} fill="url(#signupGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* DAU */}
      <div>
        <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Daily Active Users</h3>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-dau">
          {hasChartData(dau) ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={dau}>
                <defs><linearGradient id="dauGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1a3a30" stopOpacity={0.15}/><stop offset="95%" stopColor="#1a3a30" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                <YAxis tick={{ fontSize: 10 }} stroke="#cbd5e1" allowDecimals={false} />
                <Tooltip content={<ChartTooltip suffix=" users" />} />
                <Area type="monotone" dataKey="value" stroke="#1a3a30" strokeWidth={2} fill="url(#dauGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart trackingSince={trust.tracking_since} />}
        </div>
      </div>

      {/* Sessions + Avg Duration side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Sessions per Day</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-sessions">
            {hasChartData(sessions_per_day) ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={sessions_per_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 9 }} stroke="#cbd5e1" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#cbd5e1" allowDecimals={false} />
                  <Tooltip content={<ChartTooltip suffix=" sessions" />} />
                  <Bar dataKey="value" fill="#1a3a30" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart trackingSince={trust.tracking_since} />}
          </div>
        </div>
        <div>
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Avg Session (min)</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-avg-duration">
            {hasChartData(avgDurFormatted) ? (
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={avgDurFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 9 }} stroke="#cbd5e1" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#cbd5e1" />
                  <Tooltip content={<ChartTooltip suffix="m" />} />
                  <Line type="monotone" dataKey="value" stroke="#d97706" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyChart trackingSince={trust.tracking_since} />}
          </div>
        </div>
      </div>

      {/* WAU + Retention */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Weekly Active Users</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-wau">
            {hasChartData(wau) ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={wau}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="#cbd5e1" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#cbd5e1" allowDecimals={false} />
                  <Tooltip content={<ChartTooltip suffix=" users" />} />
                  <Bar dataKey="value" fill="#235347" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart trackingSince={trust.tracking_since} />}
          </div>
        </div>
        {retention.length > 0 && (
          <div>
            <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Retention (%)</h3>
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="chart-retention">
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={retention}>
                  <defs><linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={0.2}/><stop offset="95%" stopColor="#059669" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="#cbd5e1" />
                  <YAxis tick={{ fontSize: 9 }} stroke="#cbd5e1" domain={[0, 100]} />
                  <Tooltip content={<ChartTooltip suffix="%" />} />
                  <Area type="monotone" dataKey="value" stroke="#059669" strokeWidth={2} fill="url(#retGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* PART 3: Feature Usage Summary */}
      {feature_usage && feature_usage.total_users > 0 && (
        <div data-testid="feature-usage-summary">
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-3">Feature Adoption</h3>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Health Data', value: feature_usage.conditions + feature_usage.medications, users: feature_usage.users_with_health_data, icon: Heartbeat },
                { label: 'AI Chat', value: feature_usage.chat_sessions, users: feature_usage.users_with_chat, icon: ChatCircle },
                { label: 'Documents', value: feature_usage.documents, users: feature_usage.users_with_documents, icon: File },
                { label: 'Courses', value: feature_usage.courses, users: 0, icon: BookOpen },
              ].map(({ label, value, users, icon: I }) => (
                <div key={label} className="text-center">
                  <I size={18} className="text-[#1a3a30] mx-auto mb-1" />
                  <p className="text-lg font-bold text-[#0F172A] tabular-nums">{value}</p>
                  <p className="text-[10px] text-slate-500">{label}</p>
                  {users > 0 && <p className="text-[9px] text-slate-400">{users} user{users > 1 ? 's' : ''}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════
   USER DETAIL PANEL
   ═══════════════════════════════════════ */
const UserDetailPanel = ({ userId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/founder/users/${userId}/detail`)
      .then(res => setDetail(res.data))
      .catch(() => toast.error('Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="user-detail-close"><ArrowLeft size={18} className="text-slate-500" /></button>
          <h3 className="text-sm font-bold text-[#0F172A]">User Detail</h3>
          <div className="w-7" />
        </div>

        {loading ? (
          <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : detail ? (
          <div className="p-4 space-y-5">
            {/* Identity */}
            <div className="text-center pb-4 border-b border-slate-100">
              <div className="w-14 h-14 rounded-full bg-[#1a3a30]/10 flex items-center justify-center mx-auto mb-2">
                <UserCircle size={32} className="text-[#1a3a30]" />
              </div>
              <p className="text-base font-bold text-[#0F172A]">{detail.name || 'Unnamed'}</p>
              <p className="text-xs text-slate-500">{detail.email}</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${detail.archived ? 'bg-slate-200 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                  {detail.archived ? 'ARCHIVED' : 'ACTIVE'}
                </span>
                <span className="text-[10px] text-slate-400">{detail.role}</span>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-2">
              <MetricCard label="Sessions" value={detail.total_sessions} accent />
              <MetricCard label="Avg Duration" value={fmtDur(detail.avg_duration)} />
              <MetricCard label="Total Time" value={fmtDur(detail.total_duration)} />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-slate-400 font-medium">Last Active</p>
                <p className="text-slate-700 font-medium">{fmtTime(detail.last_active)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-slate-400 font-medium">Joined</p>
                <p className="text-slate-700 font-medium">{fmtDate(detail.created_at)}</p>
              </div>
            </div>

            {/* Feature Usage */}
            <div>
              <h4 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Feature Usage</h4>
              <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                {[
                  { icon: Heartbeat, label: 'Conditions', count: detail.feature_usage.conditions },
                  { icon: File, label: 'Medications', count: detail.feature_usage.medications },
                  { icon: ChatCircle, label: 'Chat Sessions', count: detail.feature_usage.chat_sessions },
                  { icon: BookOpen, label: 'Courses', count: detail.feature_usage.courses },
                  { icon: File, label: 'Documents', count: detail.feature_usage.documents },
                ].map(({ icon: I, label, count }) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2"><I size={13} className="text-slate-400" /><span className="text-xs text-slate-600">{label}</span></div>
                    <span className="text-xs font-bold text-[#0F172A] tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Session History */}
            <div>
              <h4 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Recent Sessions ({detail.recent_sessions.length})</h4>
              {detail.recent_sessions.length > 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
                  {detail.recent_sessions.map((s, i) => (
                    <div key={i} className="px-3 py-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-700 font-medium">{fmtDate(s.started_at)}</span>
                        <span className="text-xs text-slate-500 font-mono">{fmtDur(s.duration_seconds)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-slate-400">
                        <span>{fmtTime(s.started_at)}</span>
                        {s.actions?.length > 0 && <span>{s.actions.length} actions</span>}
                        {s.ended_at && <span>Ended: {fmtTime(s.ended_at)}</span>}
                      </div>
                      {s.actions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {s.actions.slice(0, 5).map((a, j) => (
                            <span key={j} className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{a.name}</span>
                          ))}
                          {s.actions.length > 5 && <span className="text-[9px] text-slate-400">+{s.actions.length - 5} more</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-3">No sessions recorded</p>
              )}
            </div>
          </div>
        ) : (
          <p className="p-6 text-sm text-slate-400">User not found</p>
        )}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   USERS TAB — Scalable
   ═══════════════════════════════════════ */
const UsersTab = ({ onRefresh }) => {
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('last_active');
  const [sortDir, setSortDir] = useState('desc');
  const [lastActiveDays, setLastActiveDays] = useState(0);
  const [detailUserId, setDetailUserId] = useState(null);
  const [archiveDialog, setArchiveDialog] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');

  const loadUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
        status: statusFilter,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (search) params.append('search', search);
      if (lastActiveDays > 0) params.append('last_active_days', lastActiveDays.toString());
      const { data } = await axios.get(`${API}/founder/users?${params}`);
      setUsers(data.users || []);
      setMetrics(data.metrics || null);
      setPagination(data.pagination || { page: 1, per_page: 50, total: 0, total_pages: 1 });
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sortBy, sortDir, lastActiveDays]);

  useEffect(() => { loadUsers(1); }, [loadUsers]);

  const handleSearch = () => setSearch(searchInput);
  const handleSearchKey = (e) => { if (e.key === 'Enter') setSearch(searchInput); };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <SortDescending size={11} className="text-slate-300 ml-0.5" />;
    return sortDir === 'desc'
      ? <SortDescending size={11} className="text-[#1a3a30] ml-0.5" weight="bold" />
      : <SortAscending size={11} className="text-[#1a3a30] ml-0.5" weight="bold" />;
  };

  const handleArchive = async () => {
    if (!archiveDialog) return;
    try {
      await axios.post(`${API}/founder/users/${archiveDialog}/archive`, { reason: archiveReason });
      toast.success('User archived');
      setArchiveDialog(null);
      setArchiveReason('');
      loadUsers(pagination.page);
    } catch { toast.error('Failed to archive'); }
  };

  const handleRestore = async (uid) => {
    try {
      await axios.post(`${API}/founder/users/${uid}/restore`);
      toast.success('User restored');
      loadUsers(pagination.page);
    } catch { toast.error('Failed to restore'); }
  };

  const statusCounts = useMemo(() => {
    if (!metrics) return {};
    return {
      active: metrics.total_users,
      inactive: 0,
      archived: 0,
    };
  }, [metrics]);

  return (
    <div className="space-y-4" data-testid="founder-users">
      {/* Trust + Metrics */}
      {metrics && (
        <>
          <TrustBadge confidence={metrics.confidence} trackingSince={metrics.tracking_since} daysTracking={metrics.days_tracking} totalEvents={metrics.total_events} />
          <div className="grid grid-cols-3 gap-2">
            <MetricCard label="Real Users" value={metrics.total_users} accent />
            <MetricCard label="Active 24h" value={metrics.active_24h} />
            <MetricCard label="Active 7d" value={metrics.active_7d} />
            <MetricCard label="Total Sessions" value={metrics.total_sessions} />
            <MetricCard label="Avg Session" value={fmtDur(metrics.avg_session_duration)} />
            <MetricCard label="Return Rate" value={`${metrics.return_rate}%`} />
          </div>
        </>
      )}

      {/* Search + Filters */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKey}
              placeholder="Search name or email..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a3a30]/10"
              style={{ fontSize: '16px' }}
              data-testid="users-search"
            />
          </div>
          <button onClick={handleSearch} className="px-3 py-2 bg-[#1a3a30] text-white text-xs font-medium rounded-lg" data-testid="users-search-btn">Search</button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            {['active', 'inactive', 'archived', 'all'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${statusFilter === f ? 'bg-white text-[#0F172A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`users-filter-${f}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <select value={lastActiveDays} onChange={e => setLastActiveDays(Number(e.target.value))}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] text-slate-600 outline-none" data-testid="users-filter-days">
            <option value={0}>Any time</option>
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={() => loadUsers(1)} className="p-1.5 hover:bg-slate-100 rounded-lg" title="Refresh">
            <ArrowsClockwise size={13} className="text-slate-400" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left text-[10px] font-semibold text-slate-400 uppercase px-3 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                    <span className="flex items-center">User <SortIcon field="name" /></span>
                  </th>
                  <th className="text-left text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('last_active')}>
                    <span className="flex items-center">Last Active <SortIcon field="last_active" /></span>
                  </th>
                  <th className="text-center text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('sessions')}>
                    <span className="flex items-center justify-center">Sessions <SortIcon field="sessions" /></span>
                  </th>
                  <th className="text-center text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5 cursor-pointer select-none" onClick={() => toggleSort('avg_duration')}>
                    <span className="flex items-center justify-center">Avg <SortIcon field="avg_duration" /></span>
                  </th>
                  <th className="text-center text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5">Total Time</th>
                  <th className="text-center text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5">Status</th>
                  <th className="text-center text-[10px] font-semibold text-slate-400 uppercase px-2 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-slate-400">No users found</td></tr>
                ) : users.map(u => {
                  const engScore = Math.min(100, (u.total_sessions * 10) + (u.activity_count * 2) + (u.avg_duration > 0 ? Math.min(u.avg_duration / 60, 30) : 0));
                  return (
                  <tr key={u.id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setDetailUserId(u.id)} data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2.5">
                      <p className="text-sm text-[#0F172A] font-medium truncate max-w-[150px]">{u.name || u.email}</p>
                      {u.name && <p className="text-[10px] text-slate-400 truncate max-w-[150px]">{u.email}</p>}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmtTime(u.last_active || u.last_login)}</td>
                    <td className="px-2 py-2.5 text-xs text-center tabular-nums font-medium">
                      {u.total_sessions > 0 ? u.total_sessions : u.activity_count > 0 ? <span className="text-blue-500" title={`${u.activity_count} platform actions`}>{u.activity_count}*</span> : '—'}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-center text-slate-500">{fmtDur(u.avg_duration)}</td>
                    <td className="px-2 py-2.5 text-xs text-center text-slate-500 tabular-nums">{fmtDur(u.total_duration)}</td>
                    <td className="px-2 py-2.5 text-center">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                        u.status === 'active' ? 'bg-emerald-50 text-emerald-600' :
                        u.status === 'recent' ? 'bg-amber-50 text-amber-600' :
                        u.status === 'seen' ? 'bg-blue-50 text-blue-600' :
                        u.status === 'archived' ? 'bg-slate-200 text-slate-500' :
                        u.status === 'never_tracked' ? 'bg-slate-100 text-slate-400' :
                        'bg-slate-100 text-slate-400'
                      }`}>{u.status === 'never_tracked' ? 'new' : u.status === 'seen' ? 'seen' : u.status}</span>
                    </td>
                    <td className="px-2 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                      {u.archived ? (
                        <button onClick={() => handleRestore(u.id)} className="text-[10px] text-[#1a3a30] font-semibold hover:underline" data-testid={`restore-${u.id}`}>
                          <ArrowCounterClockwise size={12} className="inline mr-0.5" />Restore
                        </button>
                      ) : (
                        <button onClick={() => setArchiveDialog(u.id)} className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-0.5 mx-auto" data-testid={`archive-${u.id}`}>
                          <Archive size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-500" data-testid="users-pagination">
          <span>Showing {(pagination.page - 1) * pagination.per_page + 1}–{Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => loadUsers(pagination.page - 1)} disabled={pagination.page <= 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="users-prev-page">
              <CaretLeft size={14} />
            </button>
            <span className="px-2 font-medium">Page {pagination.page} of {pagination.total_pages}</span>
            <button onClick={() => loadUsers(pagination.page + 1)} disabled={pagination.page >= pagination.total_pages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="users-next-page">
              <CaretRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Archive Confirmation Dialog */}
      {archiveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="archive-dialog">
          <div className="absolute inset-0 bg-black/30" onClick={() => setArchiveDialog(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-5 w-full max-w-sm mx-4">
            <h3 className="text-sm font-bold text-[#0F172A] mb-3">Archive User</h3>
            <p className="text-xs text-slate-500 mb-3">User data will be preserved. You can restore them at any time.</p>
            <textarea value={archiveReason} onChange={e => setArchiveReason(e.target.value)} placeholder="Reason for archiving (optional)..." rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-none mb-3" style={{ fontSize: '16px' }} data-testid="archive-reason" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setArchiveDialog(null); setArchiveReason(''); }} className="px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 rounded-lg">Cancel</button>
              <button onClick={handleArchive} className="px-4 py-2 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600" data-testid="archive-confirm">Archive</button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Side Panel */}
      <UserDetailPanel userId={detailUserId} onClose={() => setDetailUserId(null)} />
    </div>
  );
};


/* ═══════════════════════════════════════
   TASKS TAB
   ═══════════════════════════════════════ */
const TasksTab = ({ tasks, onAdd, onUpdate, onDelete }) => {
  const [text, setText] = useState('');
  const [priority, setPriority] = useState('medium');
  const [note, setNote] = useState('');
  const [showForm, setShowForm] = useState(false);
  const active = tasks.filter(t => !t.completed);
  const done = tasks.filter(t => t.completed);

  return (
    <div className="space-y-4" data-testid="founder-tasks">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[#0F172A] uppercase tracking-wider">Tasks ({active.length})</h2>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 text-sm text-[#1a3a30] font-medium" data-testid="founder-add-task-btn"><Plus size={14} /> Add</button>
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Task..." className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none" style={{ fontSize: '16px' }} data-testid="founder-task-input" />
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)..." className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          <div className="flex items-center gap-2">
            {['high', 'medium', 'low'].map(p => (
              <button key={p} onClick={() => setPriority(p)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${priority === p ? 'border-[#1a3a30] bg-[#1a3a30]/5 text-[#1a3a30]' : 'border-slate-200 text-slate-400'}`}>{p}</button>
            ))}
            <div className="flex-1" />
            <button onClick={() => { if (text.trim()) { onAdd({ text: text.trim(), priority, note: note.trim() }); setText(''); setNote(''); setShowForm(false); }}} className="bg-[#1a3a30] text-white rounded-lg px-4 py-2 text-xs font-medium" data-testid="founder-task-submit">Add</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {active.map(t => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <button onClick={() => onUpdate(t.id, { completed: true })} className="mt-0.5 w-5 h-5 rounded-full border-2 border-slate-300 hover:border-[#1a3a30] flex-shrink-0 transition-colors" data-testid={`task-complete-${t.id}`} />
            <div className="flex-1 min-w-0"><p className="text-sm text-[#0F172A]">{t.text}</p>{t.note && <p className="text-[10px] text-slate-400 mt-0.5">{t.note}</p>}</div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <PBadge p={t.priority} />
              <button onClick={() => onUpdate(t.id, { pinned: !t.pinned })} className={`p-1 rounded transition-colors ${t.pinned ? 'text-[#1a3a30]' : 'text-slate-300 hover:text-slate-500'}`}><PushPin size={13} weight={t.pinned ? 'fill' : 'regular'} /></button>
              <button onClick={() => onDelete(t.id)} className="p-1 text-slate-300 hover:text-red-400 transition-colors"><Trash size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {done.length > 0 && (
        <div>
          <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Completed ({done.length})</p>
          {done.slice(0, 10).map(t => (
            <div key={t.id} className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 flex items-center gap-3 mb-1.5">
              <Check size={14} className="text-emerald-400" weight="bold" />
              <p className="text-sm text-slate-400 line-through flex-1 truncate">{t.text}</p>
              <button onClick={() => onDelete(t.id)} className="p-1 text-slate-300 hover:text-red-400"><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════
   IDEAS TAB
   ═══════════════════════════════════════ */
const IdeasTab = ({ ideas, onAdd, onUpdate, onDelete, onConvert }) => {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const active = ideas.filter(i => !i.archived);
  const archived = ideas.filter(i => i.archived);

  return (
    <div className="space-y-4" data-testid="founder-ideas">
      <h2 className="text-sm font-bold text-[#0F172A] uppercase tracking-wider">Ideas ({active.length})</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && text.trim()) { onAdd({ text: text.trim(), note: note.trim() }); setText(''); setNote(''); }}} placeholder="Capture an idea..." className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none" style={{ fontSize: '16px' }} data-testid="founder-idea-input" />
        <div className="flex gap-2">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)..." className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          <button onClick={() => { if (text.trim()) { onAdd({ text: text.trim(), note: note.trim() }); setText(''); setNote(''); }}} className="bg-amber-500 text-white rounded-lg px-4 py-2 text-xs font-medium" data-testid="founder-idea-submit">Capture</button>
        </div>
      </div>
      <div className="space-y-2">
        {active.map(idea => (
          <div key={idea.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <Lightbulb size={14} className="text-amber-400 mt-0.5 flex-shrink-0" weight="fill" />
            <div className="flex-1 min-w-0"><p className="text-sm text-[#0F172A]">{idea.text}</p>{idea.note && <p className="text-[10px] text-slate-400 mt-0.5">{idea.note}</p>}<p className="text-[10px] text-slate-300 mt-1">{fmtTime(idea.created_at)}</p></div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => onConvert(idea.id)} className="p-1 text-slate-400 hover:text-[#1a3a30]" title="Convert to task"><ListChecks size={13} /></button>
              <button onClick={() => onUpdate(idea.id, { archived: true })} className="p-1 text-slate-300 hover:text-slate-500"><Archive size={13} /></button>
              <button onClick={() => onDelete(idea.id)} className="p-1 text-slate-300 hover:text-red-400"><Trash size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {archived.length > 0 && (
        <div>
          <p className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Archived ({archived.length})</p>
          {archived.slice(0, 5).map(idea => (
            <div key={idea.id} className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 flex items-center gap-3 mb-1.5">
              <p className="text-sm text-slate-400 flex-1 truncate">{idea.text}</p>
              <button onClick={() => onUpdate(idea.id, { archived: false })} className="text-[10px] text-slate-400 hover:text-slate-600">Restore</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════
   OPS REGISTER TAB — Enhanced
   ═══════════════════════════════════════ */
const OPS_CATS = ["Domains / DNS", "Hosting / Servers", "Databases", "Email Systems", "AI / API Providers", "Payments", "Brand Assets", "Legal / Entity", "Backups", "Monitoring", "Analytics", "Third-party Services", "Other"];
const EMPTY_OPS_FORM = { title: '', category: 'Other', purpose: '', url: '', owner: '', login_email: '', credential_location: '', mfa_method: '', recovery_method: '', renewal_date: '', notes: '', criticality: 'medium', related_links: '' };

const OpsTab = ({ records, onAdd, onUpdate, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_OPS_FORM });
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const filtered = records.filter(r => {
    if (filterCat !== 'all' && r.category !== filterCat) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.title?.toLowerCase().includes(q) || r.purpose?.toLowerCase().includes(q) || r.notes?.toLowerCase().includes(q) || r.owner?.toLowerCase().includes(q);
    }
    return true;
  });

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    if (editing) { onUpdate(editing, form); setEditing(null); } else { onAdd(form); }
    setForm({ ...EMPTY_OPS_FORM });
    setShowForm(false);
  };

  const startEdit = (r) => { setForm({ ...r }); setEditing(r.id); setShowForm(true); };

  const handleExport = () => {
    window.open(`${API}/founder/ops/export`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="founder-ops">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[#0F172A] uppercase tracking-wider">Ops Register ({records.length})</h2>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700" data-testid="ops-export">
            <Export size={13} /> Export CSV
          </button>
          <button onClick={() => { setEditing(null); setForm({ ...EMPTY_OPS_FORM }); setShowForm(!showForm); }} className="flex items-center gap-1.5 text-sm text-[#1a3a30] font-medium" data-testid="ops-add-btn"><Plus size={14} /> Add</button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search systems..." className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" style={{ fontSize: '16px' }} data-testid="ops-search" />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-2 text-xs text-slate-600 outline-none" data-testid="ops-category-filter">
          <option value="all">All Categories</option>
          {OPS_CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="System / Service name *" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none font-medium" style={{ fontSize: '16px' }} data-testid="ops-title-input" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none">
              {OPS_CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.criticality} onChange={e => setForm(f => ({ ...f, criticality: e.target.value }))} className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none">
              {['critical', 'high', 'medium', 'low'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="Purpose" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="URL" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
            <input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="Owner" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.login_email} onChange={e => setForm(f => ({ ...f, login_email: e.target.value }))} placeholder="Login email / username" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
            <input value={form.credential_location} onChange={e => setForm(f => ({ ...f, credential_location: e.target.value }))} placeholder="Where credential is stored" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.mfa_method} onChange={e => setForm(f => ({ ...f, mfa_method: e.target.value }))} placeholder="MFA method" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
            <input value={form.recovery_method} onChange={e => setForm(f => ({ ...f, recovery_method: e.target.value }))} placeholder="Recovery method" className="border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          </div>
          <input value={form.renewal_date} onChange={e => setForm(f => ({ ...f, renewal_date: e.target.value }))} placeholder="Renewal / expiry date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (do NOT store passwords)" rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none resize-none" style={{ fontSize: '16px' }} />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-2 text-xs text-slate-400 hover:bg-slate-50 rounded-lg">Cancel</button>
            <button onClick={handleSubmit} className="bg-[#1a3a30] text-white rounded-lg px-4 py-2 text-xs font-medium" data-testid="ops-submit">{editing ? 'Update' : 'Add Record'}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`ops-record-${r.id}`}>
            <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-full px-4 py-3 flex items-start justify-between text-left">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <PBadge p={r.criticality} />
                  <span className="text-[10px] text-slate-400">{r.category}</span>
                </div>
                <p className="text-sm font-medium text-[#0F172A]">{r.title}</p>
                {r.purpose && <p className="text-xs text-slate-500 mt-0.5">{r.purpose}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                {expanded === r.id ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
              </div>
            </button>
            {expanded === r.id && (
              <div className="px-4 pb-3 border-t border-slate-100 pt-3 space-y-2">
                {r.url && <div className="text-xs"><span className="text-slate-400 font-medium">URL:</span> <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">{r.url}</a></div>}
                {r.owner && <div className="text-xs"><span className="text-slate-400 font-medium">Owner:</span> <span className="text-slate-600">{r.owner}</span></div>}
                {r.login_email && <div className="text-xs"><span className="text-slate-400 font-medium">Login:</span> <span className="text-slate-600">{r.login_email}</span></div>}
                {r.credential_location && <div className="text-xs flex items-center gap-1"><Shield size={11} className="text-slate-400" /><span className="text-slate-400 font-medium">Creds:</span> <span className="text-slate-600">{r.credential_location}</span></div>}
                {r.mfa_method && <div className="text-xs"><span className="text-slate-400 font-medium">MFA:</span> <span className="text-slate-600">{r.mfa_method}</span></div>}
                {r.recovery_method && <div className="text-xs"><span className="text-slate-400 font-medium">Recovery:</span> <span className="text-slate-600">{r.recovery_method}</span></div>}
                {r.renewal_date && <div className="text-xs"><span className="text-slate-400 font-medium">Renewal:</span> <span className="text-slate-600">{r.renewal_date}</span></div>}
                {r.notes && <div className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 mt-1">{r.notes}</div>}
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => startEdit(r)} className="text-[10px] text-[#1a3a30] font-medium hover:underline">Edit</button>
                  <button onClick={() => onDelete(r.id)} className="text-[10px] text-red-400 hover:underline">Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-xs text-slate-400 bg-white border border-slate-200 rounded-xl px-4 py-6 text-center">No records found</p>}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   HANDOVER TAB — with File Uploads
   ═══════════════════════════════════════ */
const fmtSize = (bytes) => {
  if (!bytes) return '0B';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${Math.round(bytes / (1024 * 1024) * 10) / 10}MB`;
};

const HandoverTab = ({ notes, onAdd, onUpdate, onDelete, onRefreshAll }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', tags: '' });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [uploading, setUploading] = useState(null);

  const filtered = notes.filter(n => {
    if (!search) return true;
    const q = search.toLowerCase();
    return n.title?.toLowerCase().includes(q) || n.content?.toLowerCase().includes(q) || n.tags?.toLowerCase().includes(q);
  });

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    if (editing) { onUpdate(editing, form); setEditing(null); } else { onAdd(form); }
    setForm({ title: '', content: '', tags: '' });
    setShowForm(false);
  };

  const startEdit = (n) => { setForm({ title: n.title, content: n.content, tags: n.tags }); setEditing(n.id); setShowForm(true); };

  const handleFileUpload = async (noteId, file) => {
    setUploading(noteId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/founder/handover/${noteId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${file.name}`);
      onRefreshAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  };

  const handleFileDelete = async (noteId, fileId) => {
    try {
      await axios.delete(`${API}/founder/handover/${noteId}/files/${fileId}`);
      toast.success('File removed');
      onRefreshAll();
    } catch {
      toast.error('Failed to remove file');
    }
  };

  const handleFileDownload = (noteId, fileId, filename) => {
    window.open(`${API}/founder/handover/${noteId}/files/${fileId}`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="founder-handover">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[#0F172A] uppercase tracking-wider">Handover Notes ({notes.length})</h2>
        <button onClick={() => { setEditing(null); setForm({ title: '', content: '', tags: '' }); setShowForm(!showForm); }} className="flex items-center gap-1.5 text-sm text-[#1a3a30] font-medium"><Plus size={14} /> Add</button>
      </div>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search notes..." className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none" style={{ fontSize: '16px' }} />
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Title *" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none font-medium" style={{ fontSize: '16px' }} />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Content..." rows={6} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-none" style={{ fontSize: '16px' }} />
          <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma-separated)" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none" style={{ fontSize: '16px' }} />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-2 text-xs text-slate-400">Cancel</button>
            <button onClick={handleSubmit} className="bg-[#1a3a30] text-white rounded-lg px-4 py-2 text-xs font-medium">{editing ? 'Update' : 'Save Note'}</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {filtered.map(n => (
          <div key={n.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <button onClick={() => setExpanded(expanded === n.id ? null : n.id)} className="w-full px-4 py-3 flex items-center justify-between text-left">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-[#0F172A]">{n.title}</p>
                  {n.files?.length > 0 && <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium">{n.files.length} file{n.files.length > 1 ? 's' : ''}</span>}
                </div>
                {n.tags && <div className="flex gap-1 mt-1">{n.tags.split(',').map((t, i) => <span key={i} className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{t.trim()}</span>)}</div>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-[10px] text-slate-300">{fmtTime(n.updated_at)}</span>
                {expanded === n.id ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
              </div>
            </button>
            {expanded === n.id && (
              <div className="px-4 pb-3 border-t border-slate-100 pt-3 space-y-3">
                {n.content && <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{n.content}</div>}

                {/* Files Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Attachments</p>
                    <label className="flex items-center gap-1 text-[10px] text-[#1a3a30] font-medium cursor-pointer hover:underline">
                      <Plus size={11} />
                      {uploading === n.id ? 'Uploading...' : 'Add file'}
                      <input type="file" className="hidden" accept=".pdf,.docx,.txt,.csv,.png,.jpg,.jpeg,.xlsx"
                        disabled={uploading === n.id}
                        onChange={e => { if (e.target.files?.[0]) handleFileUpload(n.id, e.target.files[0]); e.target.value = ''; }}
                        data-testid={`handover-file-input-${n.id}`} />
                    </label>
                  </div>
                  {n.files?.length > 0 ? (
                    <div className="space-y-1.5">
                      {n.files.map(f => (
                        <div key={f.file_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`file-${f.file_id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <File size={14} className="text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-slate-700 truncate">{f.filename}</p>
                              <p className="text-[9px] text-slate-400">{fmtSize(f.size_bytes)} · {f.file_type} · {fmtTime(f.uploaded_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => handleFileDownload(n.id, f.file_id, f.filename)} className="p-1 text-slate-400 hover:text-[#1a3a30]" title="Download" data-testid={`download-${f.file_id}`}>
                              <Export size={12} />
                            </button>
                            <button onClick={() => handleFileDelete(n.id, f.file_id)} className="p-1 text-slate-300 hover:text-red-400" title="Remove" data-testid={`delete-file-${f.file_id}`}>
                              <Trash size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-300">No files attached</p>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <button onClick={() => startEdit(n)} className="text-[10px] text-[#1a3a30] font-medium hover:underline">Edit</button>
                  <button onClick={() => onDelete(n.id)} className="text-[10px] text-red-400 hover:underline">Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   BILLING TAB (Founder)
   ═══════════════════════════════════════ */
const FounderBillingTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState('');
  const [foundational, setFoundational] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/founder/billing-stats`),
        axios.get(`${API}/founder/foundational-users`),
      ]);
      setStats(s.data);
      setFoundational(f.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grantFoundational = async (userId) => {
    try {
      await axios.post(`${API}/founder/users/${userId}/grant-foundational`);
      toast.success('Foundational granted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const revokeFoundational = async (userId) => {
    try {
      await axios.post(`${API}/founder/users/${userId}/revoke-foundational`);
      toast.success('Foundational revoked');
      load();
    } catch { toast.error('Failed'); }
  };

  const extendTrial = async (userId, days) => {
    try {
      await axios.post(`${API}/founder/users/${userId}/extend-trial`, { days });
      toast.success(`Trial extended by ${days} days`);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-slate-200 rounded-xl animate-pulse" />)}</div>;

  const s = stats?.summary || {};

  return (
    <div className="space-y-5" data-testid="founder-billing">
      {/* Overview */}
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="Total Users" value={s.total_users || 0} accent />
        <MetricCard label="Foundational" value={`${s.foundational || 0} / ${s.foundational_limit}`} />
        <MetricCard label="Trial Active" value={s.trial_active || 0} />
        <MetricCard label="Paid" value={s.active_paid || 0} />
        <MetricCard label="Conversion" value={`${s.conversion_rate || 0}%`} />
        <MetricCard label="Churn" value={`${s.churn_rate || 0}%`} />
      </div>

      {/* Foundational Users */}
      <div>
        <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Foundational Users ({foundational?.count || 0} / 50)</h3>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {foundational?.users?.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {foundational.users.map(u => (
                <div key={u.id} className="px-4 py-2.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#0F172A] font-medium">{u.name || u.email}</p>
                    {u.name && <p className="text-[10px] text-slate-400">{u.email}</p>}
                  </div>
                  <button onClick={() => revokeFoundational(u.id)} className="text-[10px] text-red-400 hover:text-red-600">Revoke</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="px-4 py-4 text-xs text-slate-400 text-center">No foundational users yet</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-1">Trial Expired</p>
          <p className="text-xl font-bold text-[#0F172A]">{s.trial_expired || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mb-1">Canceled</p>
          <p className="text-xl font-bold text-[#0F172A]">{s.canceled || 0}</p>
        </div>
      </div>

      {/* Recent Transactions */}
      {stats?.recent_transactions?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider mb-2">Recent Transactions</h3>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
            {stats.recent_transactions.slice(0, 5).map((t, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between text-xs">
                <div><span className="text-slate-700">{t.email}</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">${t.amount} {t.currency?.toUpperCase()}</span>
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${t.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{t.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════
   MAIN FOUNDER PAGE
   ═══════════════════════════════════════ */
const FounderPage = () => {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('analytics');
  const [tasks, setTasks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [opsRecords, setOpsRecords] = useState([]);
  const [handoverNotes, setHandoverNotes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [userMetrics, setUserMetrics] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [t, i, o, h] = await Promise.all([
        axios.get(`${API}/founder/tasks`),
        axios.get(`${API}/founder/ideas`),
        axios.get(`${API}/founder/ops`),
        axios.get(`${API}/founder/handover`),
      ]);
      setTasks(t.data);
      setIdeas(i.data);
      setOpsRecords(o.data.records || []);
      setHandoverNotes(h.data);
    } catch (err) {
      if (err.response?.status === 403) return;
    } finally {
      setDataLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [analyticsRes, usersRes] = await Promise.all([
        axios.get(`${API}/founder/analytics?days=30`),
        axios.get(`${API}/founder/users?status=all&per_page=1`),
      ]);
      setAnalytics(analyticsRes.data);
      setUserMetrics(usersRes.data.metrics || null);
    } catch (err) {
      if (err.response?.status !== 403) console.error('Analytics load failed', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFounder(user)) {
      loadData();
      loadAnalytics();
    }
  }, [user, loadData, loadAnalytics]);

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><p className="text-sm text-slate-400">Loading...</p></div>;
  if (!isFounder(user)) return <Navigate to="/home" replace />;

  // Handlers
  const addTask = async (t) => { try { const { data } = await axios.post(`${API}/founder/tasks`, t); setTasks(p => [data, ...p]); toast.success('Task added'); } catch { toast.error('Failed'); }};
  const updTask = async (id, u) => { try { await axios.patch(`${API}/founder/tasks/${id}`, u); setTasks(p => p.map(t => t.id === id ? { ...t, ...u } : t)); } catch { toast.error('Failed'); }};
  const delTask = async (id) => { try { await axios.delete(`${API}/founder/tasks/${id}`); setTasks(p => p.filter(t => t.id !== id)); } catch { toast.error('Failed'); }};
  const addIdea = async (i) => { try { const { data } = await axios.post(`${API}/founder/ideas`, i); setIdeas(p => [data, ...p]); toast.success('Captured'); } catch { toast.error('Failed'); }};
  const updIdea = async (id, u) => { try { await axios.patch(`${API}/founder/ideas/${id}`, u); setIdeas(p => p.map(i => i.id === id ? { ...i, ...u } : i)); } catch { toast.error('Failed'); }};
  const delIdea = async (id) => { try { await axios.delete(`${API}/founder/ideas/${id}`); setIdeas(p => p.filter(i => i.id !== id)); } catch { toast.error('Failed'); }};
  const convertIdea = async (id) => { try { const { data } = await axios.post(`${API}/founder/ideas/${id}/to-task`); setTasks(p => [data, ...p]); setIdeas(p => p.map(i => i.id === id ? { ...i, archived: true } : i)); toast.success('Converted'); } catch { toast.error('Failed'); }};
  const addOps = async (r) => { try { const { data } = await axios.post(`${API}/founder/ops`, r); setOpsRecords(p => [data, ...p]); toast.success('Record added'); } catch { toast.error('Failed'); }};
  const updOps = async (id, u) => { try { await axios.patch(`${API}/founder/ops/${id}`, u); setOpsRecords(p => p.map(r => r.id === id ? { ...r, ...u } : r)); toast.success('Updated'); } catch { toast.error('Failed'); }};
  const delOps = async (id) => { try { await axios.delete(`${API}/founder/ops/${id}`); setOpsRecords(p => p.filter(r => r.id !== id)); } catch { toast.error('Failed'); }};
  const addNote = async (n) => { try { const { data } = await axios.post(`${API}/founder/handover`, n); setHandoverNotes(p => [data, ...p]); toast.success('Saved'); } catch { toast.error('Failed'); }};
  const updNote = async (id, u) => { try { await axios.patch(`${API}/founder/handover/${id}`, u); setHandoverNotes(p => p.map(n => n.id === id ? { ...n, ...u } : n)); toast.success('Updated'); } catch { toast.error('Failed'); }};
  const delNote = async (id) => { try { await axios.delete(`${API}/founder/handover/${id}`); setHandoverNotes(p => p.filter(n => n.id !== id)); } catch { toast.error('Failed'); }};

  const TABS = [
    { id: 'analytics', label: 'Analytics', icon: ChartLine },
    { id: 'activation', label: 'Activation', icon: Funnel },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'tasks', label: 'Tasks', icon: ListChecks },
    { id: 'ideas', label: 'Ideas', icon: Lightbulb },
    { id: 'ops', label: 'Ops', icon: Wrench },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'handover', label: 'Handover', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="founder-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Founder</h1>
            <button onClick={() => { loadData(); loadAnalytics(); }} className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="founder-refresh">
              <ArrowsClockwise size={12} /> Refresh
            </button>
          </div>
          <div className="flex gap-1 overflow-x-auto pb-0.5 -mx-1 px-1">
            {TABS.map(t => (
              <Tab key={t.id} active={tab === t.id} label={t.label} icon={t.icon} onClick={() => setTab(t.id)} testId={`founder-tab-${t.id}`} />
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 pb-20">
        {dataLoading && tab !== 'analytics' && tab !== 'users' ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-slate-200 rounded-xl animate-pulse" />)}</div>
        ) : (
          <>
            {tab === 'analytics' && <AnalyticsTab analytics={analytics} loading={analyticsLoading} onRefresh={loadAnalytics} userMetrics={userMetrics} />}
            {tab === 'activation' && <ActivationTab />}
            {tab === 'users' && <UsersTab onRefresh={loadData} />}
            {tab === 'tasks' && <TasksTab tasks={tasks} onAdd={addTask} onUpdate={updTask} onDelete={delTask} />}
            {tab === 'ideas' && <IdeasTab ideas={ideas} onAdd={addIdea} onUpdate={updIdea} onDelete={delIdea} onConvert={convertIdea} />}
            {tab === 'ops' && <OpsTab records={opsRecords} onAdd={addOps} onUpdate={updOps} onDelete={delOps} />}
            {tab === 'billing' && <FounderBillingTab />}
            {tab === 'handover' && <HandoverTab notes={handoverNotes} onAdd={addNote} onUpdate={updNote} onDelete={delNote} onRefreshAll={loadData} />}
          </>
        )}
      </div>
    </div>
  );
};

export default FounderPage;

/* ═══════════════════════════════════════════════════════════
   FeedbackCard — live chat thumbs-up/down summary.
   ═══════════════════════════════════════════════════════════ */
const FeedbackCard = ({ days = 30 }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/founder/chat-feedback?days=${days}`).then(r => setD(r.data)).catch(() => {});
  }, [days]);
  if (!d) return <div className="h-28 bg-white border border-slate-200 rounded-xl animate-pulse" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="founder-feedback-card">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Chat feedback</p>
        <span className="text-[10px] text-slate-400">last {days} days</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><p className="text-xs text-slate-500 mb-0.5">Thumbs up</p><p className="text-lg font-semibold text-[#0f1a17]">{d.up}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">Thumbs down</p><p className="text-lg font-semibold text-[#0f1a17]">{d.down}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">Helpful %</p><p className="text-lg font-semibold text-[#0f1a17]">{d.helpful_pct}%</p></div>
      </div>
      {d.recent_thumbs_down && d.recent_thumbs_down.length > 0 && (
        <details className="pt-2 border-t border-slate-100">
          <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-[#235347]">Recent thumbs-down ({d.recent_thumbs_down.length})</summary>
          <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
            {d.recent_thumbs_down.map((f, i) => (
              <div key={i} className="bg-slate-50 rounded p-2 text-[11px]">
                <div className="flex justify-between gap-2 text-slate-400 mb-1">
                  <span>{(f.created_at || '').slice(5, 16).replace('T', ' ')}</span>
                  <span className="truncate">{f.user_email}</span>
                </div>
                <p className="text-slate-700 leading-relaxed">{f.content_excerpt}</p>
                {f.reason && <p className="text-amber-800 mt-1">Reason: {f.reason}</p>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RetentionCard — D1/D7/D30 cohorts + DAU/WAU/MAU.
   ═══════════════════════════════════════════════════════════ */
const RetentionCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/founder/retention`).then(r => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="h-44 bg-white border border-slate-200 rounded-xl animate-pulse" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="founder-retention-card">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Retention</p>
      <div className="grid grid-cols-3 gap-3">
        <div><p className="text-xs text-slate-500 mb-0.5">Users total</p><p className="text-lg font-semibold text-[#0f1a17]">{d.users.total}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">New today</p><p className="text-lg font-semibold text-[#0f1a17]">{d.users.new_today}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">New 7d</p><p className="text-lg font-semibold text-[#0f1a17]">{d.users.new_this_week}</p></div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100">
        <div><p className="text-xs text-slate-500 mb-0.5">DAU</p><p className="text-base font-semibold text-[#0f1a17]">{d.active.DAU}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">WAU</p><p className="text-base font-semibold text-[#0f1a17]">{d.active.WAU}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">MAU</p><p className="text-base font-semibold text-[#0f1a17]">{d.active.MAU}</p></div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100">
        <div>
          <p className="text-xs text-slate-500 mb-0.5">D1 retention</p>
          <p className="text-base font-semibold text-[#0f1a17]">{d.retention.D1.pct}%</p>
          <p className="text-[10px] text-slate-400">{d.retention.D1.returned}/{d.retention.D1.eligible} returned</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">D7 retention</p>
          <p className="text-base font-semibold text-[#0f1a17]">{d.retention.D7.pct}%</p>
          <p className="text-[10px] text-slate-400">{d.retention.D7.returned}/{d.retention.D7.eligible} returned</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-0.5">D30 retention</p>
          <p className="text-base font-semibold text-[#0f1a17]">{d.retention.D30.pct}%</p>
          <p className="text-[10px] text-slate-400">{d.retention.D30.returned}/{d.retention.D30.eligible} returned</p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PasswordMetricsCard — used inside ActivationTab.
   Shows reset-request → completion funnel + recent events.
   ═══════════════════════════════════════════════════════════ */
const PasswordMetricsCard = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${API}/founder/password-metrics?days=${days}`);
      setData(res);
    } catch (e) {
      setError('Failed to load — ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-32 bg-white border border-slate-200 rounded-xl animate-pulse" />;
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs">{error}</div>;
  if (!data) return null;

  const c = data.counts || {};
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="password-metrics-card">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Password resets</p>
        <span className="text-[10px] text-slate-400">last {days} days</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><p className="text-xs text-slate-500 mb-0.5">Requested</p><p className="text-lg font-semibold text-[#0f1a17]">{data.requested}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">Completed</p><p className="text-lg font-semibold text-[#0f1a17]">{data.completed}</p></div>
        <div><p className="text-xs text-slate-500 mb-0.5">Completion</p><p className="text-lg font-semibold text-[#0f1a17]">{data.completion_pct}%</p></div>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
        <div className="text-[11px]"><span className="text-slate-400">Unknown-email probes</span><span className="float-right text-slate-700 font-medium">{c.reset_requested_unknown || 0}</span></div>
        <div className="text-[11px]"><span className="text-slate-400">Rate-limited</span><span className="float-right text-slate-700 font-medium">{c.rate_limited || 0}</span></div>
        <div className="text-[11px]"><span className="text-slate-400">Expired tokens</span><span className="float-right text-slate-700 font-medium">{c.reset_expired_token || 0}</span></div>
        <div className="text-[11px]"><span className="text-slate-400">Invalid tokens</span><span className="float-right text-slate-700 font-medium">{c.reset_invalid_token || 0}</span></div>
        <div className="text-[11px]"><span className="text-slate-400">In-app changes</span><span className="float-right text-slate-700 font-medium">{c.change_completed || 0}</span></div>
        <div className="text-[11px]"><span className="text-slate-400">Wrong current-pw</span><span className="float-right text-slate-700 font-medium">{c.change_wrong_current || 0}</span></div>
      </div>
      {data.recent_events && data.recent_events.length > 0 && (
        <details className="pt-2 border-t border-slate-100">
          <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-[#235347]">Recent events ({data.recent_events.length})</summary>
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto" data-testid="password-metrics-events">
            {data.recent_events.slice(0, 20).map((ev, i) => (
              <div key={i} className="text-[11px] font-mono flex gap-2 text-slate-500">
                <span className="w-32 flex-shrink-0 text-slate-400">{(ev.created_at || '').slice(5, 16).replace('T', ' ')}</span>
                <span className="w-40 flex-shrink-0 text-[#235347]">{ev.event}</span>
                <span className="truncate">{ev.user_email || '—'}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RetentionMetricsCard — Phase-2 retention engine signals
   (Checklist + Weekly Digest + Web Vitals).
   Fetches GET /api/founder/retention-metrics?days=N.
   ═══════════════════════════════════════════════════════════ */
const RetentionMetricsCard = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data: res } = await axios.get(`${API}/founder/retention-metrics?days=${days}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return <div className="h-48 bg-white border border-slate-200 rounded-xl animate-pulse" data-testid="founder-retention-loading" />;
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs">Retention failed — {error}</div>;
  }

  const chk = data.checklist || {};
  const dg = data.digest || {};
  const v = data.vitals || {};

  const fmt = (x) => (x === null || x === undefined ? '–' : x);
  const lcpTone = v.lcp?.p75 == null ? 'text-slate-400' : v.lcp.p75 < 2500 ? 'text-emerald-700' : v.lcp.p75 < 4000 ? 'text-amber-700' : 'text-red-700';
  const inpTone = v.inp?.p75 == null ? 'text-slate-400' : v.inp.p75 < 200 ? 'text-emerald-700' : v.inp.p75 < 500 ? 'text-amber-700' : 'text-red-700';
  const clsTone = v.cls?.p75 == null ? 'text-slate-400' : v.cls.p75 < 0.1 ? 'text-emerald-700' : v.cls.p75 < 0.25 ? 'text-amber-700' : 'text-red-700';

  return (
    <div className="space-y-3" data-testid="founder-retention-card">
      {/* Checklist */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">First-session checklist</p>
          <span className="text-[10px] text-slate-300">Last {data.days}d</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-checklist-viewed">
            <p className="text-base font-semibold text-[#0f1a17] tabular-nums">{chk.viewed_users}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Users saw it</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-checklist-completed">
            <p className="text-base font-semibold text-[#235347] tabular-nums">{chk.completed_users}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Completed all</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-checklist-items">
            <p className="text-base font-semibold text-[#0f1a17] tabular-nums">{chk.items_completed_events}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Items done</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-checklist-rate">
            <p className={`text-base font-semibold tabular-nums ${chk.completion_rate_pct >= 60 ? 'text-emerald-700' : chk.completion_rate_pct >= 30 ? 'text-amber-700' : 'text-slate-500'}`}>{chk.completion_rate_pct}%</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Completion</p>
          </div>
        </div>
      </div>

      {/* Weekly digest */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Weekly digest</p>
          <span className="text-[10px] text-slate-300">Last {data.days}d</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-digest-sent">
            <p className="text-base font-semibold text-[#0f1a17] tabular-nums">{dg.sent_users}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Sent</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-digest-opened">
            <p className="text-base font-semibold text-[#0f1a17] tabular-nums">{dg.opened_users}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Opened</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-digest-clicked">
            <p className="text-base font-semibold text-[#0f1a17] tabular-nums">{dg.clicked_users}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Clicked</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-digest-open-rate">
            <p className={`text-base font-semibold tabular-nums ${dg.open_rate_pct >= 30 ? 'text-emerald-700' : 'text-slate-500'}`}>{dg.open_rate_pct}%</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Open rate</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-digest-click-rate">
            <p className={`text-base font-semibold tabular-nums ${dg.click_rate_pct >= 10 ? 'text-emerald-700' : 'text-slate-500'}`}>{dg.click_rate_pct}%</p>
            <p className="text-[10px] text-slate-400 mt-0.5">CTR (of opens)</p>
          </div>
        </div>
      </div>

      {/* Web Vitals */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Mobile Web Vitals (p75)</p>
          <span className="text-[10px] text-slate-300">Last {data.days}d</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-vitals-lcp">
            <p className={`text-base font-semibold tabular-nums ${lcpTone}`}>{fmt(v.lcp?.p75)}{v.lcp?.p75 != null ? 'ms' : ''}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">LCP · {v.lcp?.good_pct ?? 0}% good · n={v.lcp?.samples ?? 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-vitals-inp">
            <p className={`text-base font-semibold tabular-nums ${inpTone}`}>{fmt(v.inp?.p75)}{v.inp?.p75 != null ? 'ms' : ''}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">INP · {v.inp?.good_pct ?? 0}% good · n={v.inp?.samples ?? 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid="founder-vitals-cls">
            <p className={`text-base font-semibold tabular-nums ${clsTone}`}>{fmt(v.cls?.p75)}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">CLS · {v.cls?.good_pct ?? 0}% good · n={v.cls?.samples ?? 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DemoFunnelCard — Compact metrics strip for the /demo investor tour.
   Shown at the top of the Activation tab.
   Fetches GET /api/founder/demo-funnel?days=N.
   ═══════════════════════════════════════════════════════════ */
const DemoFunnelCard = ({ days }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data: res } = await axios.get(`${API}/founder/demo-funnel?days=${days}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse" data-testid="founder-demo-funnel-loading">
        <div className="h-4 w-32 bg-slate-100 rounded mb-3" />
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-14 bg-slate-50 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs" data-testid="founder-demo-funnel-error">
        Demo funnel failed — {error}
      </div>
    );
  }

  const metrics = [
    { key: 'demo_started',        label: 'Tour started',   val: data.demo_started,        tone: 'text-[#0f1a17]' },
    { key: 'demo_completed',      label: 'Tour completed', val: data.demo_completed,      tone: 'text-[#235347]' },
    { key: 'completion_pct',      label: 'Completion %',   val: `${data.completion_pct}%`, tone: data.completion_pct >= 60 ? 'text-emerald-700' : data.completion_pct >= 30 ? 'text-amber-700' : 'text-slate-500' },
    { key: 'clicked_start_free',  label: 'Start Free',     val: data.clicked_start_free,  tone: 'text-[#235347]' },
    { key: 'clicked_demo_account',label: 'Demo Account',   val: data.clicked_demo_account,tone: 'text-[#235347]' },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-4" data-testid="founder-demo-funnel-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Investor demo tour</p>
          <p className="text-[11px] text-slate-400 mt-0.5">30-second guided experience at /demo</p>
        </div>
        <span className="text-[10px] text-slate-300">Last {data.days} days</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {metrics.map((m) => (
          <div key={m.key} className="bg-slate-50 rounded-lg px-3 py-2.5 text-center" data-testid={`founder-demo-metric-${m.key}`}>
            <p className={`text-base font-semibold tabular-nums ${m.tone}`}>{m.val}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>
      {data.demo_cta_screen_viewed > 0 && (
        <p className="text-[10px] text-slate-400 mt-3">
          {data.demo_cta_screen_viewed} hit the CTA screen · {data.cta_click_pct}% tapped a CTA
        </p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ActivationTab — 8-step funnel drop-off report.
   Fetches GET /api/founder/activation-funnel?days=N, renders each
   step with count, conversion-from-previous %, and drop-off count.
   ═══════════════════════════════════════════════════════════ */
const ActivationTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (d) => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${API}/founder/activation-funnel?days=${d}`);
      setData(res);
    } catch (e) {
      setError('Failed to load funnel — ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const steps = data?.steps || [];
  const top = steps[0]?.count || 0;

  return (
    <div className="space-y-4" data-testid="founder-activation-tab">
      {/* Range selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${days === d ? 'bg-[#235347] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#235347]/30'}`}
              data-testid={`activation-range-${d}`}
            >
              {d} days
            </button>
          ))}
        </div>
        <button
          onClick={() => load(days)}
          className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1"
          data-testid="activation-refresh"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs">
          {error}
        </div>
      )}

      {/* Demo tour funnel — shown above the main activation funnel */}
      <DemoFunnelCard days={days} />

      {/* Phase-2 retention engine: checklist, digest, vitals */}
      <RetentionMetricsCard days={days} />

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-14 bg-white border border-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : steps.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-8 text-center">
          <p className="text-xs text-slate-400">No events recorded yet for this range</p>
        </div>
      ) : (
        <>
          {/* Top-level insight */}
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Top of funnel</p>
            <p className="text-[13px] text-slate-600">
              <span className="text-lg font-semibold text-[#0f1a17]">{top}</span> distinct visitors hit the homepage in the last {days} days
            </p>
          </div>

          {/* Funnel steps */}
          <div className="space-y-2" data-testid="activation-funnel-steps">
            {steps.map((s, i) => {
              const widthPct = top ? Math.max(4, (s.count / top) * 100) : 0;
              const dropHigh = i > 0 && s.step_conversion_pct < 50;
              return (
                <div key={s.event} className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`activation-step-${s.event}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-[10px] text-slate-400 font-mono w-5 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <p className="text-[13px] font-medium text-[#0f1a17] truncate">{s.label}</p>
                    </div>
                    <div className="flex items-baseline gap-3 flex-shrink-0">
                      <span className="text-[15px] font-semibold text-[#0f1a17] tabular-nums">{s.count}</span>
                      <span className="text-[11px] text-slate-400 tabular-nums w-14 text-right">{s.pct_of_top.toFixed(1)}% of top</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#235347] rounded-full transition-all"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  {i > 0 && (
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-[10px] font-medium ${dropHigh ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {s.step_conversion_pct.toFixed(1)}% from prev step
                      </span>
                      {s.drop_off_count > 0 && (
                        <span className="text-[10px] text-slate-400">
                          {s.drop_off_count} dropped off
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-[10px] text-slate-400 text-center pt-2">
            Distinct identity per step (anon_id pre-register, user_id after). Range: last {days} days.
          </p>

          {/* Password reset metrics */}
          <div className="pt-4">
            <PasswordMetricsCard days={days} />
          </div>

          {/* Retention cohorts (D1 / D7 / D30) */}
          <div className="pt-2">
            <RetentionCard />
          </div>

          {/* Chat feedback (thumbs up/down) */}
          <div className="pt-2">
            <FeedbackCard days={days} />
          </div>
        </>
      )}
    </div>
  );
};
